/**
 * useOfferImage Hook
 *
 * Resolves the best available image for a funnel offer.
 * If the offer already has an imageUrl (set by funnels-go enrichment),
 * uses it directly. Otherwise, falls back to the first image from the
 * product catalog via products-go.
 */

import { useState, useEffect } from 'react';
import { productService } from '@/infrastructure/config/services.config';

export function useOfferImage(
  imageUrl: string | undefined,
  productId: string | undefined,
): string | null {
  const [resolved, setResolved] = useState<string | null>(imageUrl || null);

  useEffect(() => {
    if (imageUrl) {
      setResolved(imageUrl);
      return;
    }

    // Reset to null when both inputs are falsy (prevents stale image from previous offer)
    setResolved(null);

    if (!productId) return;

    let cancelled = false;
    productService.getProductById(productId).then((product) => {
      if (!cancelled && product?.images?.[0]?.src) {
        setResolved(product.images[0].src);
      }
    }).catch(() => {
      // Silent — backend should have enriched; this is a last-resort fallback
    });

    return () => { cancelled = true; };
  }, [imageUrl, productId]);

  return resolved;
}
